import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, Camera, Check, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basics', title: 'Basic Info', description: 'Tell us about yourself' },
  { id: 'seeking', title: 'Who You Seek', description: 'Who are you looking for?' },
  { id: 'photos', title: 'Add Photos', description: 'Show your best self' },
  { id: 'culture', title: 'Cultural Background', description: 'Share your heritage' },
  { id: 'intent', title: 'Your Intent', description: 'What are you looking for?' },
];

const LANGUAGES = ['English', 'French', 'Swahili', 'Yoruba', 'Hausa', 'Amharic', 'Zulu', 'Igbo', 'Arabic', 'Portuguese', 'Spanish'];
const RELIGIONS = ['Christianity', 'Islam', 'Traditional African', 'Hinduism', 'Buddhism', 'Other', 'Prefer not to say'];
const INTENTS = [
  { id: 'marriage', label: 'Marriage-Minded', emoji: '💍', description: 'Serious commitment' },
  { id: 'cultural_exchange', label: 'Cultural Exchange', emoji: '🌍', description: 'Learn and share cultures' },
  { id: 'long_distance', label: 'Long-Distance', emoji: '✈️', description: 'Open to distance' },
  { id: 'travel_partner', label: 'Travel Partner', emoji: '🧳', description: 'Explore together' },
];

const COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Ethiopia', 'Tanzania', 
  'Uganda', 'Senegal', 'Cameroon', 'Ivory Coast', 'Morocco', 'Egypt',
  'USA', 'UK', 'Canada', 'Germany', 'France', 'Australia', 'Netherlands',
  'Italy', 'Spain', 'Sweden', 'Norway', 'Belgium', 'Switzerland'
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    bio: '',
    birth_date: '',
    location_country: '',
    location_city: '',
    photos: [],
    languages: [],
    religion: '',
    tribe_region: '',
    intent: '',
    occupation: '',
    education: '',
    height_cm: '',
    has_children: null,
    wants_children: null,
    // New fields
    seeking: user?.gender === 'female' ? 'men' : 'women',
    distance_preference: 'worldwide',
    age_preference_min: 25,
    age_preference_max: 50
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleLanguage = (lang) => {
    const current = formData.languages;
    if (current.includes(lang)) {
      handleChange('languages', current.filter(l => l !== lang));
    } else {
      handleChange('languages', [...current, lang]);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await profileAPI.uploadPhoto(formDataUpload);
      const photoUrl = response.data.url;
      
      handleChange('photos', [...formData.photos, photoUrl]);
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index) => {
    const newPhotos = [...formData.photos];
    newPhotos.splice(index, 1);
    handleChange('photos', newPhotos);
  };

  const handleNext = () => {
    // Validation for each step
    if (currentStep === 0) {
      if (!formData.birth_date) {
        toast.error('Please enter your birthday');
        return;
      }
      if (!formData.location_country) {
        toast.error('Please select your country');
        return;
      }
    }
    
    if (currentStep === 1 && !formData.seeking) {
      toast.error('Please select who you are seeking');
      return;
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.intent) {
      toast.error('Please select your intent');
      return;
    }

    setLoading(true);
    try {
      // Calculate age from birth_date
      const birthDate = new Date(formData.birth_date);
      const age = Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      
      const profileData = {
        ...formData,
        height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
        age: age
      };
      
      const response = await profileAPI.create(profileData);
      updateProfile(response.data.profile);
      toast.success('Profile created successfully!');
      navigate('/discover');
    } catch (error) {
      console.error('Profile creation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const step = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  // Calculate profile completeness
  const completenessFields = ['bio', 'birth_date', 'location_country', 'photos', 'languages', 'intent', 'occupation'];
  const completedFields = completenessFields.filter(field => {
    const value = formData[field];
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  });
  const completenessPercent = Math.round((completedFields.length / completenessFields.length) * 100);

  return (
    <div className="min-h-screen bg-dark flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className="p-2 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="text-center">
          <span className="text-sm text-gray-400 font-body">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <div className="text-xs text-gold mt-1">{completenessPercent}% Complete</div>
        </div>
        <button
          onClick={() => navigate('/discover')}
          className="text-gold text-sm font-body"
        >
          Skip
        </button>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-dark-elevated mx-6 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gold"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="max-w-md mx-auto"
        >
          <h1 className="font-heading text-2xl text-white mb-2">{step.title}</h1>
          <p className="text-gray-400 font-body mb-8">{step.description}</p>

          {/* Step 1: Basic Info */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">Birthday *</label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleChange('birth_date', e.target.value)}
                  className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl"
                  data-testid="birthdate-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">Country *</label>
                <select
                  value={formData.location_country}
                  onChange={(e) => handleChange('location_country', e.target.value)}
                  className="w-full bg-dark-elevated border border-white/10 text-white h-12 rounded-xl px-4 focus:outline-none focus:border-gold/50"
                  data-testid="country-select"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">City (optional)</label>
                <Input
                  type="text"
                  placeholder="e.g., Lagos, New York"
                  value={formData.location_city}
                  onChange={(e) => handleChange('location_city', e.target.value)}
                  className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl placeholder:text-gray-500"
                  data-testid="city-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2 font-body">Occupation</label>
                  <Input
                    type="text"
                    placeholder="Your job"
                    value={formData.occupation}
                    onChange={(e) => handleChange('occupation', e.target.value)}
                    className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl placeholder:text-gray-500"
                    data-testid="occupation-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2 font-body">Height (cm)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 175"
                    value={formData.height_cm}
                    onChange={(e) => handleChange('height_cm', e.target.value)}
                    className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl placeholder:text-gray-500"
                    data-testid="height-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Who You're Seeking */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">I am looking for *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'women', label: 'Women', emoji: '👩' },
                    { id: 'men', label: 'Men', emoji: '👨' },
                    { id: 'both', label: 'Both', emoji: '👥' }
                  ].map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleChange('seeking', option.id)}
                      className={`p-4 rounded-xl border text-center transition-colors ${
                        formData.seeking === option.id
                          ? 'bg-gold/10 border-gold'
                          : 'bg-dark-elevated border-white/10 hover:border-gold/30'
                      }`}
                      data-testid={`seeking-${option.id}`}
                    >
                      <span className="text-2xl block mb-1">{option.emoji}</span>
                      <span className={`font-body text-sm ${
                        formData.seeking === option.id ? 'text-gold' : 'text-white'
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">
                  Age range: {formData.age_preference_min} - {formData.age_preference_max}
                </label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="18"
                    max="80"
                    value={formData.age_preference_min}
                    onChange={(e) => handleChange('age_preference_min', parseInt(e.target.value))}
                    className="bg-dark-elevated border-white/10 text-white h-10 rounded-xl w-20 text-center"
                  />
                  <span className="text-gray-400">to</span>
                  <Input
                    type="number"
                    min="18"
                    max="80"
                    value={formData.age_preference_max}
                    onChange={(e) => handleChange('age_preference_max', parseInt(e.target.value))}
                    className="bg-dark-elevated border-white/10 text-white h-10 rounded-xl w-20 text-center"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">Distance preference</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'local', label: 'Same City' },
                    { id: 'country', label: 'Same Country' },
                    { id: 'continent', label: 'Same Continent' },
                    { id: 'worldwide', label: 'Worldwide' }
                  ].map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleChange('distance_preference', option.id)}
                      className={`px-4 py-3 rounded-xl text-sm font-body transition-colors ${
                        formData.distance_preference === option.id
                          ? 'bg-gold text-black'
                          : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">Children preference</label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-dark-elevated rounded-xl p-3 border border-white/10">
                    <span className="text-white font-body text-sm">Do you have children?</span>
                    <div className="flex gap-2">
                      {[true, false].map(val => (
                        <button
                          key={String(val)}
                          type="button"
                          onClick={() => handleChange('has_children', val)}
                          className={`px-3 py-1 rounded-full text-xs transition-colors ${
                            formData.has_children === val
                              ? 'bg-gold text-black'
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          {val ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-dark-elevated rounded-xl p-3 border border-white/10">
                    <span className="text-white font-body text-sm">Do you want children?</span>
                    <div className="flex gap-2">
                      {[true, false].map(val => (
                        <button
                          key={String(val)}
                          type="button"
                          onClick={() => handleChange('wants_children', val)}
                          className={`px-3 py-1 rounded-full text-xs transition-colors ${
                            formData.wants_children === val
                              ? 'bg-gold text-black'
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          {val ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Photos */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div
                    key={index}
                    className={`aspect-square rounded-xl border-2 border-dashed relative overflow-hidden transition-colors ${
                      formData.photos[index]
                        ? 'border-gold'
                        : 'border-white/20 hover:border-gold/50'
                    }`}
                    data-testid={`photo-slot-${index}`}
                  >
                    {formData.photos[index] ? (
                      <>
                        <img
                          src={formData.photos[index].startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${formData.photos[index]}`
                            : formData.photos[index]
                          }
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-1 left-1 text-[10px] bg-gold text-black px-1.5 py-0.5 rounded">
                            Main
                          </span>
                        )}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-full flex flex-col items-center justify-center"
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? (
                          <Loader2 className="w-6 h-6 text-gold animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-6 h-6 text-gray-500" />
                            {index === 0 && (
                              <span className="text-[10px] text-gray-500 mt-1">Main</span>
                            )}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto || formData.photos.length >= 6}
                variant="outline"
                className="w-full border-gold/30 text-gold hover:bg-gold/10"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
              </Button>
              
              <p className="text-center text-gray-400 text-sm font-body">
                Add at least 1 photo to continue. First photo will be your main profile picture.
              </p>

              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">About you</label>
                <textarea
                  placeholder="Write a short bio about yourself... What makes you unique? What are you looking for?"
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full bg-dark-elevated border border-white/10 text-white rounded-xl p-4 placeholder:text-gray-500 focus:outline-none focus:border-gold/50 font-body"
                  data-testid="bio-input"
                />
                <p className="text-right text-xs text-gray-500 mt-1">
                  {formData.bio.length}/500
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Cultural Background */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">Languages you speak</label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-4 py-2 rounded-full text-sm font-body transition-colors ${
                        formData.languages.includes(lang)
                          ? 'bg-gold text-black'
                          : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                      }`}
                      data-testid={`lang-${lang.toLowerCase()}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-3 font-body">Religion</label>
                <div className="grid grid-cols-2 gap-2">
                  {RELIGIONS.map(religion => (
                    <button
                      key={religion}
                      type="button"
                      onClick={() => handleChange('religion', religion)}
                      className={`px-4 py-3 rounded-xl text-sm font-body transition-colors ${
                        formData.religion === religion
                          ? 'bg-gold text-black'
                          : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                      }`}
                      data-testid={`religion-${religion.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {religion}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">Tribe/Region (optional)</label>
                <Input
                  type="text"
                  placeholder="e.g., Yoruba, Kikuyu, Zulu"
                  value={formData.tribe_region}
                  onChange={(e) => handleChange('tribe_region', e.target.value)}
                  className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl placeholder:text-gray-500"
                  data-testid="tribe-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">Education</label>
                <select
                  value={formData.education}
                  onChange={(e) => handleChange('education', e.target.value)}
                  className="w-full bg-dark-elevated border border-white/10 text-white h-12 rounded-xl px-4 focus:outline-none focus:border-gold/50"
                >
                  <option value="">Select education level</option>
                  <option value="high_school">High School</option>
                  <option value="some_college">Some College</option>
                  <option value="bachelors">Bachelor's Degree</option>
                  <option value="masters">Master's Degree</option>
                  <option value="doctorate">Doctorate</option>
                  <option value="trade_school">Trade School</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 5: Intent */}
          {currentStep === 4 && (
            <div className="space-y-4">
              {INTENTS.map(intent => (
                <button
                  key={intent.id}
                  type="button"
                  onClick={() => handleChange('intent', intent.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-colors ${
                    formData.intent === intent.id
                      ? 'bg-gold/10 border-gold'
                      : 'bg-dark-elevated border-white/10 hover:border-gold/30'
                  }`}
                  data-testid={`intent-${intent.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{intent.emoji}</span>
                    <div className="flex-1">
                      <span className={`font-body font-medium block ${
                        formData.intent === intent.id ? 'text-gold' : 'text-white'
                      }`}>
                        {intent.label}
                      </span>
                      <span className="text-gray-400 text-sm">{intent.description}</span>
                    </div>
                    {formData.intent === intent.id && (
                      <Check className="w-5 h-5 text-gold" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="p-6 bg-dark border-t border-white/5">
        <Button
          onClick={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}
          disabled={loading}
          className="w-full h-12 btn-gold rounded-full font-body font-semibold flex items-center justify-center gap-2"
          data-testid="next-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating profile...
            </>
          ) : currentStep === STEPS.length - 1 ? (
            <>Complete Profile</>
          ) : (
            <>Continue <ArrowRight className="w-5 h-5" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
